import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { supabase } from '../../../utils/supabaseClient';
import { SaveIcon, UserIcon } from '../../../utils/IconWrapper';

const Container = styled(motion.div)`
  ${commonStyles.card}
  width: 600px;
  max-width: 90vw;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};
  padding-bottom: ${designSystem.spacing.lg};
  border-bottom: 2px solid ${designSystem.colors.border.light};

  h2 {
    ${commonStyles.titleMedium}
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: ${designSystem.typography.fontSize.xl};
  cursor: pointer;
  color: ${designSystem.colors.text.secondary};
  transition: all ${designSystem.transitions.normal};
  
  &:hover {
    color: ${designSystem.colors.text.primary};
    transform: scale(1.1);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.xl};
  padding: ${designSystem.spacing.lg};
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${designSystem.colors.background.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: #000000;
    font-size: ${designSystem.typography.fontSize.lg};
  }

  .details {
    .name {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.semibold};
      margin-bottom: 4px;
    }
    
    .email {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }
`;

const CurrentRatingSection = styled.div`
  margin-bottom: ${designSystem.spacing.xl};

  h3 {
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.lg};
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
  }
`;

const RatingOverview = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.lg};

  @media (max-width: ${designSystem.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const OverallRating = styled.div`
  text-align: center;
  padding: ${designSystem.spacing.lg};
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.lg};

  .rating-value {
    font-size: ${designSystem.typography.fontSize['3xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .stars {
    font-size: ${designSystem.typography.fontSize.xl};
    color: ${designSystem.colors.status.warning};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .count {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
  }
`;

const RatingBreakdown = styled.div`
  .category {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${designSystem.spacing.md};
    padding: ${designSystem.spacing.sm} 0;

    .label {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
    }

    .value {
      display: flex;
      align-items: center;
      gap: ${designSystem.spacing.sm};

      .stars {
        color: ${designSystem.colors.status.warning};
      }

      .number {
        color: ${designSystem.colors.text.secondary};
        font-size: ${designSystem.typography.fontSize.sm};
        min-width: 30px;
      }
    }
  }
`;

const RecentReviewsSection = styled.div`
  margin-bottom: ${designSystem.spacing.xl};

  h4 {
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.lg};
  }
`;

const ReviewCard = styled.div`
  background: ${designSystem.colors.background.tertiary};
  padding: ${designSystem.spacing.lg};
  border-radius: ${designSystem.borderRadius.lg};
  margin-bottom: ${designSystem.spacing.md};
  border-left: 4px solid ${designSystem.colors.primary.main};

  .review-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${designSystem.spacing.md};

    .property {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
    }

    .date {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }

  .rating {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
    margin-bottom: ${designSystem.spacing.sm};

    .stars {
      color: ${designSystem.colors.status.warning};
    }

    .value {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.bold};
    }
  }

  .comment {
    color: ${designSystem.colors.text.secondary};
    line-height: 1.6;
    font-style: italic;
  }
`;

const AddRatingSection = styled.div`
  border-top: 2px solid ${designSystem.colors.border.light};
  padding-top: ${designSystem.spacing.xl};

  h4 {
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.lg};
  }
`;

const RatingForm = styled.form`
  .form-group {
    margin-bottom: ${designSystem.spacing.lg};

    label {
      display: block;
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
      margin-bottom: ${designSystem.spacing.sm};
    }

    .rating-input {
      display: flex;
      gap: ${designSystem.spacing.sm};
      align-items: center;
      margin-bottom: ${designSystem.spacing.sm};

      .star-rating {
        display: flex;
        gap: 4px;
      }

      .rating-value {
        color: ${designSystem.colors.text.secondary};
        font-size: ${designSystem.typography.fontSize.sm};
        margin-left: ${designSystem.spacing.md};
      }
    }

    textarea {
      ${commonStyles.input}
      min-height: 100px;
      resize: vertical;
    }

    select {
      ${commonStyles.input}
    }
  }
`;

const StarButton = styled.button<{ filled: boolean }>`
  background: transparent;
  border: none;
  font-size: ${designSystem.typography.fontSize.xl};
  cursor: pointer;
  color: ${props => props.filled ? designSystem.colors.status.warning : designSystem.colors.text.secondary};
  transition: all ${designSystem.transitions.fast};
  
  &:hover {
    transform: scale(1.2);
    color: ${designSystem.colors.status.warning};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
  justify-content: flex-end;
  margin-top: ${designSystem.spacing.xl};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  ${props => props.variant === 'secondary' ? commonStyles.buttonSecondary : commonStyles.buttonPrimary}
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
`;

interface RatingSystemProps {
  userId: string;
  onClose: () => void;
}

interface UserData {
  id: string;
  full_name: string;
  email: string;
}

interface Rating {
  id: string;
  overall_rating: number;
  cleanliness: number;
  communication: number;
  respect_rules: number;
  property_care: number;
  comment: string;
  accommodation_title: string;
  created_at: string;
}

const RatingSystem: React.FC<RatingSystemProps> = ({ userId, onClose }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRating, setNewRating] = useState({
    overall_rating: 0,
    cleanliness: 0,
    communication: 0,
    respect_rules: 0,
    property_care: 0,
    comment: '',
    accommodation_id: ''
  });
  const [accommodations, setAccommodations] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      // Carica dati utente
      const { data: user } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // Carica ratings esistenti (simulati)
      const mockRatings: Rating[] = [
        {
          id: '1',
          overall_rating: 4.5,
          cleanliness: 5,
          communication: 4,
          respect_rules: 5,
          property_care: 4,
          comment: 'Ospite eccellente, molto rispettoso e pulito. Appartamento lasciato in perfette condizioni.',
          accommodation_title: 'Appartamento Centro Storico',
          created_at: '2024-01-15'
        },
        {
          id: '2',
          overall_rating: 4.0,
          cleanliness: 4,
          communication: 4,
          respect_rules: 4,
          property_care: 4,
          comment: 'Buon ospite, comunicazione tempestiva e rispettoso delle regole.',
          accommodation_title: 'Loft Moderno',
          created_at: '2024-01-10'
        }
      ];

      // Carica alloggi disponibili
      const { data: accommodations_data } = await supabase
        .from('accommodations')
        .select('id, title_it');

      setUserData(user);
      setRatings(mockRatings);
      setAccommodations(accommodations_data || []);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageRatings = () => {
    if (ratings.length === 0) {
      return {
        overall: 0,
        cleanliness: 0,
        communication: 0,
        respect_rules: 0,
        property_care: 0,
        count: 0
      };
    }

    const totals = ratings.reduce((acc, rating) => ({
      overall: acc.overall + rating.overall_rating,
      cleanliness: acc.cleanliness + rating.cleanliness,
      communication: acc.communication + rating.communication,
      respect_rules: acc.respect_rules + rating.respect_rules,
      property_care: acc.property_care + rating.property_care
    }), { overall: 0, cleanliness: 0, communication: 0, respect_rules: 0, property_care: 0 });

    const count = ratings.length;

    return {
      overall: totals.overall / count,
      cleanliness: totals.cleanliness / count,
      communication: totals.communication / count,
      respect_rules: totals.respect_rules / count,
      property_care: totals.property_care / count,
      count
    };
  };

  const handleStarClick = (category: string, value: number) => {
    setNewRating(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // In produzione, salverebbe nel database
      const ratingData = {
        ...newRating,
        user_id: userId,
        created_at: new Date().toISOString(),
        id: Date.now().toString()
      };

      // Aggiunge alla lista locale per demo
      const accommodation = accommodations.find(a => a.id === newRating.accommodation_id);
      const newRatingWithDetails: Rating = {
        ...ratingData,
        accommodation_title: accommodation?.title_it || 'Alloggio'
      };

      setRatings(prev => [newRatingWithDetails, ...prev]);
      
      // Reset form
      setNewRating({
        overall_rating: 0,
        cleanliness: 0,
        communication: 0,
        respect_rules: 0,
        property_care: 0,
        comment: '',
        accommodation_id: ''
      });

      alert('Rating aggiunto con successo!');
    } catch (error) {
      console.error('Errore salvataggio rating:', error);
      alert('Errore durante il salvataggio del rating');
    }
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return '★'.repeat(fullStars) + 
           (hasHalfStar ? '☆' : '') + 
           '☆'.repeat(emptyStars);
  };

  const renderStarInput = (category: string, value: number) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map(star => (
          <StarButton
            key={star}
            type="button"
            filled={star <= value}
            onClick={() => handleStarClick(category, star)}
          >
            ★
          </StarButton>
        ))}
      </div>
    );
  };

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['2xl'], marginBottom: designSystem.spacing.md }}>
            ⏳
          </div>
          Caricamento sistema di rating...
        </div>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['2xl'], marginBottom: designSystem.spacing.md }}>
            ❌
          </div>
          Utente non trovato
        </div>
      </Container>
    );
  }

  const averages = calculateAverageRatings();

  return (
    <Container
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      <Header>
        <h2>
          ⭐ Sistema di Rating
        </h2>
        <CloseButton onClick={onClose}>✖️</CloseButton>
      </Header>

      <UserInfo>
        <div className="avatar">
          {getInitials(userData.full_name, userData.email)}
        </div>
        <div className="details">
          <div className="name">{userData.full_name || 'Nome non specificato'}</div>
          <div className="email">{userData.email}</div>
        </div>
      </UserInfo>

      <CurrentRatingSection>
        <h3>📊 Rating Attuale</h3>
        
        <RatingOverview>
          <OverallRating>
            <div className="rating-value">
              {averages.count > 0 ? averages.overall.toFixed(1) : '—'}
            </div>
            <div className="stars">
              {averages.count > 0 ? renderStars(averages.overall) : '☆☆☆☆☆'}
            </div>
            <div className="count">
              {averages.count} review{averages.count !== 1 ? 's' : ''}
            </div>
          </OverallRating>

          <RatingBreakdown>
            <div className="category">
              <span className="label">🧹 Pulizia</span>
              <div className="value">
                <span className="stars">{averages.count > 0 ? renderStars(averages.cleanliness) : '☆☆☆☆☆'}</span>
                <span className="number">{averages.count > 0 ? averages.cleanliness.toFixed(1) : '—'}</span>
              </div>
            </div>
            <div className="category">
              <span className="label">💬 Comunicazione</span>
              <div className="value">
                <span className="stars">{averages.count > 0 ? renderStars(averages.communication) : '☆☆☆☆☆'}</span>
                <span className="number">{averages.count > 0 ? averages.communication.toFixed(1) : '—'}</span>
              </div>
            </div>
            <div className="category">
              <span className="label">📋 Rispetto Regole</span>
              <div className="value">
                <span className="stars">{averages.count > 0 ? renderStars(averages.respect_rules) : '☆☆☆☆☆'}</span>
                <span className="number">{averages.count > 0 ? averages.respect_rules.toFixed(1) : '—'}</span>
              </div>
            </div>
            <div className="category">
              <span className="label">🏠 Cura Proprietà</span>
              <div className="value">
                <span className="stars">{averages.count > 0 ? renderStars(averages.property_care) : '☆☆☆☆☆'}</span>
                <span className="number">{averages.count > 0 ? averages.property_care.toFixed(1) : '—'}</span>
              </div>
            </div>
          </RatingBreakdown>
        </RatingOverview>
      </CurrentRatingSection>

      <RecentReviewsSection>
        <h4>📝 Review Recenti</h4>
        {ratings.length > 0 ? (
          ratings.slice(0, 3).map(rating => (
            <ReviewCard key={rating.id}>
              <div className="review-header">
                <span className="property">{rating.accommodation_title}</span>
                <span className="date">{new Date(rating.created_at).toLocaleDateString('it-IT')}</span>
              </div>
              <div className="rating">
                <span className="stars">{renderStars(rating.overall_rating)}</span>
                <span className="value">{rating.overall_rating.toFixed(1)}</span>
              </div>
              {rating.comment && (
                <div className="comment">"{rating.comment}"</div>
              )}
            </ReviewCard>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: designSystem.spacing.lg, color: designSystem.colors.text.secondary }}>
            Nessuna review disponibile
          </div>
        )}
      </RecentReviewsSection>

      <AddRatingSection>
        <h4>➕ Aggiungi Nuovo Rating</h4>
        
        <RatingForm onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Alloggio</label>
            <select
              value={newRating.accommodation_id}
              onChange={(e) => setNewRating(prev => ({ ...prev, accommodation_id: e.target.value }))}
              required
            >
              <option value="">Seleziona alloggio...</option>
              {accommodations.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.title_it}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Rating Complessivo</label>
            <div className="rating-input">
              {renderStarInput('overall_rating', newRating.overall_rating)}
              <span className="rating-value">
                {newRating.overall_rating > 0 ? `${newRating.overall_rating}/5` : 'Non valutato'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>🧹 Pulizia</label>
            <div className="rating-input">
              {renderStarInput('cleanliness', newRating.cleanliness)}
              <span className="rating-value">
                {newRating.cleanliness > 0 ? `${newRating.cleanliness}/5` : 'Non valutato'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>💬 Comunicazione</label>
            <div className="rating-input">
              {renderStarInput('communication', newRating.communication)}
              <span className="rating-value">
                {newRating.communication > 0 ? `${newRating.communication}/5` : 'Non valutato'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>📋 Rispetto delle Regole</label>
            <div className="rating-input">
              {renderStarInput('respect_rules', newRating.respect_rules)}
              <span className="rating-value">
                {newRating.respect_rules > 0 ? `${newRating.respect_rules}/5` : 'Non valutato'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>🏠 Cura della Proprietà</label>
            <div className="rating-input">
              {renderStarInput('property_care', newRating.property_care)}
              <span className="rating-value">
                {newRating.property_care > 0 ? `${newRating.property_care}/5` : 'Non valutato'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Commento (opzionale)</label>
            <textarea
              value={newRating.comment}
              onChange={(e) => setNewRating(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Aggiungi un commento dettagliato sulla permanenza dell'ospite..."
            />
          </div>

          <ActionButtons>
            <ActionButton type="button" variant="secondary" onClick={onClose}>
              Annulla
            </ActionButton>
            <ActionButton type="submit">
              <SaveIcon size={16} />
              Salva Rating
            </ActionButton>
          </ActionButtons>
        </RatingForm>
      </AddRatingSection>
    </Container>
  );
};

export default RatingSystem; 